import { useCallback, useEffect, useRef, useState } from 'react';
import { drawMandelbrotFractal, applyPanAndRenderEdges } from './fractal_math';
import { getWebGLContext, drawMandelbrotFractalWebGL, disposeWebGLPanCache,
  disposeWebGLZoomCache, disposeWebGLResources,
  discardWebGLZoomTransition, presentWebGLZoomSource, presentWebGLOriginalSource,
  needsDeepZoom, canRenderWebGLPan } from './fractalWebGL';
import { useFractalWorker } from './useFractalWorker';
import { panEdgeAnchor, presentPanTiles } from './fractalPixels';
import { canCertifyCpuPalette, planExactCpuPan } from './cpuExactPan';
import { workerRenderRoute } from './cpuRenderRoute';
import { getCpuExactFrame, setCpuExactFrame, clearCpuExactFrame } from './cpuExactFrameCache';
import { cameraFromRanges } from './fractalCamera';
import { MAX_ITERATIONS, SMOOTHING_STEPS, isPatternMode } from './fractalIteration';
import { createNumberMotionLoop } from './numberMotionLoop';
import { createCpuMotionLoop } from './cpuMotionLoop';

function sameRanges(first, second) {
  return first && second && ['xRange', 'yRange'].every(axis =>
    first[axis]?.length === 2 && second[axis]?.length === 2 &&
    first[axis][0] === second[axis][0] && first[axis][1] === second[axis][1]);
}

const copyRanges = ranges => ({ xRange: [...ranges.xRange], yRange: [...ranges.yRange] });

function needsBoundedGpuMotion(canvas, ranges, options, colorParams) {
  if (isPatternMode(colorParams?.mode)) return false;
  return !!(options?.interactiveExact || options?.cachePan) && needsDeepZoom(
    ranges.xRange, ranges.yRange, canvas.width, canvas.height,
    canvas._fractalDeepZoom, canvas._fractalFloatPrecision ?? 23) &&
    !(options?.cachePan && options?.pan && canRenderWebGLPan(canvas,
      ranges.xRange, ranges.yRange, colorParams));
}

// Keep a truthful sampling magnitude even when a Decimal span is below the
// Number range. This describes the original grid; it never changes the camera.
function decimalLog10(value) {
  const [coefficient, exponent = '0'] = String(value).toLowerCase().split('e');
  const [integer, fraction = ''] = coefficient.replace(/^\+/, '').split('.');
  const digits = integer + fraction;
  const first = digits.search(/[1-9]/);
  if (first < 0) return -Infinity;
  const significant = digits.slice(first, first + 16);
  const mantissa = Number(`${significant[0]}.${significant.slice(1)}`);
  return Number(exponent) + integer.length - first - 1 + Math.log10(mantissa);
}

function planCanvasFromCamera(source, target) {
  if (!source || !target) return false;
  const oldX = source.xRange[1] - source.xRange[0];
  const oldY = source.yRange[1] - source.yRange[0];
  const newX = target.xRange[1] - target.xRange[0];
  const newY = target.yRange[1] - target.yRange[0];
  if (![oldX, oldY, newX, newY].every(value => Number.isFinite(value) && value > 0)) return false;
  const scaleX = oldX / newX;
  const scaleY = oldY / newY;
  const offsetX = (source.xRange[0] - target.xRange[0]) / newX;
  const offsetY = (source.yRange[0] - target.yRange[0]) / newY;
  if (![scaleX, scaleY, offsetX, offsetY].every(Number.isFinite)) return null;
  return { scaleX, scaleY, offsetX, offsetY };
}

function scaleCanvasFromCamera(canvas, source, target) {
  const projection = planCanvasFromCamera(source, target);
  if (!projection) return false;
  const { scaleX, scaleY, offsetX, offsetY } = projection;
  canvas.style.transformOrigin = '0 0';
  canvas.style.transform = `translate(${offsetX * 100}%, ${offsetY * 100}%) scale(${scaleX}, ${scaleY})`;
  return true;
}

function releaseZoomFence(active) {
  if (!active.sync) return;
  try { active.gl.deleteSync(active.sync); } catch (error) { /* Context may be lost. */ }
  active.sync = null;
}

function isCanvasVisible(canvas) {
  if (!canvas || document.hidden) return false;
  const rect = canvas.getBoundingClientRect();
  const bottom = rect.bottom ?? ((rect.top ?? 0) + (rect.height ?? 0));
  return bottom >= 0 && (rect.top ?? 0) <= window.innerHeight;
}

// The old renderer remains event-driven (only visible canvases receive render
// events). Deep numerical work has its own controller and never reaches here.
export function useFractalRenderer(canvasRef, { initialRanges, colorFunc, colorParams, paletteParams,
  cpuReusePaletteArray = false, cpuExactPanReuse = false, cpuInteriorDispatch = false,
  preferredRenderer, onFallback, onCpuFrameReady }) {
  const [canvasEpoch, setCanvasEpoch] = useState(0);
  const [recovering, setRecovering] = useState(false);
  const forceCanvas = useRef(false);
  const requested = useRef(initialRanges);
  const completed = useRef(initialRanges);
  const completedImage = useRef(null);
  const rendered = useRef(false);
  const pan = useRef(false);
  const settledPan = useRef(false);
  const cpuExactOwner = useRef(Symbol('cpu-exact-frame'));
  const cpuPreview = useRef(false);
  const enabled = useRef(true);
  const generation = useRef(0);
  const gpuProbe = useRef(null);
  const gpuPending = useRef(null);
  const gpuMotion = useRef(null);
  const gpuRequestOptions = useRef();
  const drawGPURef = useRef(null);
  const fallbackRef = useRef(null);
  const cpuRenderFrame = useRef(null);
  const cpuJob = useRef(null);
  const cpuMotion = useRef(null);
  const cpuSource = useRef(null);
  const cpuRequestOptions = useRef({});
  const offloadedSnapshotRef = useRef(null);
  const mounted = useRef(false);
  const paramsRef = useRef(colorParams);
  paramsRef.current = colorParams;
  const colorFuncRef = useRef(colorFunc);
  colorFuncRef.current = colorFunc;
  const onCpuFrameReadyRef = useRef(onCpuFrameReady);
  onCpuFrameReadyRef.current = onCpuFrameReady;
  const { renderFull, renderTile, renderPan, renderExactPan, cancelRender, shouldUseWorker } = useFractalWorker();

  const scheduleCpuRender = useCallback(() => {
    if (cpuRenderFrame.current !== null) return;
    cpuRenderFrame.current = requestAnimationFrame(time => {
      cpuRenderFrame.current = null;
      const canvas = canvasRef.current;
      if (enabled.current && isCanvasVisible(canvas))
        canvas.dispatchEvent(new CustomEvent('render', { detail: { presentationTime: time } }));
    });
  }, [canvasRef]);

  const commitGPUFrame = useCallback((canvas, ranges, frame = canvas._fractalZoomCache?.frame) => {
    completed.current = ranges;
    completedImage.current = { width: frame?.width ?? canvas.width, height: frame?.height ?? canvas.height,
      colorFunc: colorFuncRef.current };
    rendered.current = true;
    setRecovering(false);
  }, []);

  const stopGPUProbe = useCallback(() => {
    const active = gpuProbe.current;
    gpuProbe.current = null;
    gpuPending.current = null;
    if (!active) return;
    active.canvas._fractalZoomPending = null;
    cancelAnimationFrame(active.frame);
    releaseZoomFence(active);
  }, []);

  // Keep one full-resolution GPU draw in flight and coalesce subsequent input.
  // Readiness controls submission only; sharp pixels never wait behind a fade.
  const sampleGPUFrame = useCallback(canvas => {
    const gl = canvas._fractalZoomCache?.gl;
    if (gpuProbe.current || !gl?.fenceSync || !gl.clientWaitSync || !gl.deleteSync ||
        gl.SYNC_GPU_COMMANDS_COMPLETE == null) return;
    const active = { gl, canvas, width: canvas.width, height: canvas.height,
      start: performance.now(), frame: null, sync: null };
    try {
      active.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
      gl.flush?.();
    } catch (error) { releaseZoomFence(active); return; }
    if (!active.sync) return;
    gpuProbe.current = active;
    const poll = () => {
      if (gpuProbe.current !== active) return;
      if (canvas !== canvasRef.current || canvas.width !== active.width ||
          canvas.height !== active.height || document.hidden || gl.isContextLost?.()) {
        stopGPUProbe();
        rendered.current = false;
        return;
      }
      let status;
      try { status = gl.clientWaitSync(active.sync, 0, 0); } catch (error) { status = null; }
      if (status === gl.TIMEOUT_EXPIRED && performance.now() - active.start < 5000) {
        active.frame = requestAnimationFrame(poll);
        return;
      }
      const pending = gpuPending.current;
      stopGPUProbe();
      if (status !== gl.ALREADY_SIGNALED && status !== gl.CONDITION_SATISFIED) {
        fallbackRef.current?.(canvas, requested.current);
        return;
      }
      if (pending) {
        rendered.current = false;
        if (enabled.current && isCanvasVisible(canvas))
          drawGPURef.current?.(canvas, pending.ranges, pending.options);
      }
    };
    active.frame = requestAnimationFrame(poll);
  }, [canvasRef, stopGPUProbe]);

  const fallback = useCallback((canvas, ranges) => {
    if (canvas !== canvasRef.current || forceCanvas.current) return;
    stopGPUProbe();
    gpuMotion.current?.loop.dispose();
    gpuMotion.current = null;
    discardWebGLZoomTransition(canvas);
    forceCanvas.current = true;
    requested.current = onFallback?.() || ranges;
    completed.current = null;
    completedImage.current = null;
    rendered.current = false;
    pan.current = false;
    cpuPreview.current = false;
    setRecovering(true);
    setCanvasEpoch(value => value + 1);
  }, [canvasRef, onFallback, stopGPUProbe]);
  fallbackRef.current = fallback;

  const drawGPU = useCallback((canvas, ranges, options) => {
    if (forceCanvas.current) return false;
    const boundedMotion = needsBoundedGpuMotion(canvas, ranges, options, paramsRef.current);
    if (gpuMotion.current && (!boundedMotion || gpuMotion.current.canvas !== canvas ||
        gpuMotion.current.width !== canvas.width || gpuMotion.current.height !== canvas.height)) {
      gpuMotion.current.loop.dispose();
      gpuMotion.current = null;
    }
    if (boundedMotion && !gpuMotion.current) {
      stopGPUProbe();
      let loop;
      try {
        loop = createNumberMotionLoop(canvas, { isVisible: isCanvasVisible,
          onFrame: frame => {
            if (gpuMotion.current?.loop === loop && enabled.current && canvas === canvasRef.current &&
                frame.paletteKey === JSON.stringify(paramsRef.current || {}) &&
                sameRanges(frame, requested.current))
              commitGPUFrame(canvas, { xRange: frame.xRange, yRange: frame.yRange });
          },
          onError: () => {
            if (gpuMotion.current?.loop === loop) fallbackRef.current?.(canvas, requested.current);
          } });
      } catch (error) { loop = null; }
      if (loop) gpuMotion.current = { canvas, width: canvas.width, height: canvas.height, loop };
      else if ((canvas._fractalContext || getWebGLContext(canvas))?.gl?.getParameter) {
        // Float orbit storage and asynchronous fences are required for bounded
        // Number work. Replace an unsupported GL canvas with the CPU canvas.
        fallback(canvas, ranges);
        return false;
      }
    }
    if (gpuMotion.current) {
      rendered.current = false;
      gpuMotion.current.ranges = copyRanges(ranges);
      gpuMotion.current.paletteKey = JSON.stringify(paramsRef.current || {});
      return gpuMotion.current.loop.request(ranges, paramsRef.current, {
        interactive: !!options?.interactiveExact || !!options?.cachePan,
        anchor: options?.anchor && [options.anchor[0] * canvas.width, options.anchor[1] * canvas.height],
        presentationTime: options?.presentationTime,
      });
    }
    const active = gpuProbe.current;
    if (active && (active.canvas !== canvas || active.width !== canvas.width ||
        active.height !== canvas.height)) stopGPUProbe();
    if (gpuProbe.current?.canvas === canvas) {
      gpuPending.current = { ranges, options };
      canvas._fractalZoomPending = { startedAt: gpuProbe.current.start,
        requestedRanges: ranges };
      // A temporary projection supplies immediate motion while the one older
      // GPU submission finishes. It is never the gesture's rendering mode.
      const cache = canvas._fractalZoomCache;
      if (cache?.sourceTexture && cache.frame?.width === canvas.width &&
          cache.frame.height === canvas.height &&
          cache.frame.paletteKey === JSON.stringify(paramsRef.current || {})) {
        try {
          if (!drawMandelbrotFractalWebGL(canvas, ranges.xRange, ranges.yRange, paramsRef.current,
            { interactiveZoom: true })) {
            fallback(canvas, ranges);
            return false;
          }
        } catch (error) { fallback(canvas, ranges); return false; }
      }
      rendered.current = true;
      return true;
    }
    let ok = false;
    const cameraRequestedAt = Number.isFinite(options?.presentationTime)
      ? options.presentationTime : performance.now();
    try {
      const { anchor: _anchor, presentationTime: _presentationTime, ...drawOptions } = options || {};
      ok = drawMandelbrotFractalWebGL(canvas, ranges.xRange, ranges.yRange, paramsRef.current,
        { ...drawOptions, zoomTransition: false, twoFrameCache: true });
    } catch (error) {
      console.warn('WebGL render failed, falling back to canvas:', error);
    }
    if (!ok) fallback(canvas, ranges);
    else {
      const frame = options?.cachePan && options?.pan
        ? canvas._fractalPanCache?.frame : canvas._fractalZoomCache?.frame;
      if (frame) frame.cameraRequestedAt = cameraRequestedAt;
      canvas._fractalMotionStats = { renderer: 'direct-webgl',
        requestedRanges: copyRanges(ranges), requestedCameraAt: cameraRequestedAt,
        presentedAt: performance.now(),
        timingNote: 'GPU submission; asynchronous fence coalesces pending input.' };
      commitGPUFrame(canvas, frame
        ? { xRange: frame.xRange, yRange: frame.yRange } : ranges, frame);
      sampleGPUFrame(canvas);
    }
    return ok;
  }, [canvasRef, commitGPUFrame, fallback, sampleGPUFrame, stopGPUProbe]);
  drawGPURef.current = drawGPU;

  const present = useCallback((ranges, { interactive = false, pan: isPan = false, defer = false,
    anchor, presentationTime } = {}) => {
    const canvas = canvasRef.current;
    const waitForVisibility = defer || !isCanvasVisible(canvas);
    if (canvas) delete canvas._fractalSourceProjection;
    enabled.current = true;
    requested.current = ranges;
    cpuRequestOptions.current = { interactive, anchor: anchor && {
      x: anchor[0] * (canvas?.width || 0), y: anchor[1] * (canvas?.height || 0) } };
    if (isPan && interactive && completed.current && canvas) {
      const projection = planCanvasFromCamera(completed.current, ranges);
      const edge = projection && panEdgeAnchor([projection.offsetX, projection.offsetY,
        projection.offsetX + projection.scaleX, projection.offsetY + projection.scaleY]);
      if (edge) cpuRequestOptions.current.anchor = { x: edge[0] * canvas.width, y: edge[1] * canvas.height };
    }
    if (canvas?._wasOffloadedWebGL && preferredRenderer === 'webgl' && !forceCanvas.current) {
      if (waitForVisibility) {
        rendered.current = false;
        return;
      }
      canvas._wasOffloadedWebGL = false;
      rendered.current = false;
      setCanvasEpoch(value => value + 1);
      return;
    }
    if (canvas?._useWebGL && !forceCanvas.current) {
      gpuRequestOptions.current = isPan && interactive ? { cachePan: true, pan: true, anchor } :
        interactive ? { interactiveExact: true, anchor } : anchor ? { anchor } : undefined;
      generation.current += 1;
      cancelRender();
      if (waitForVisibility) {
        gpuMotion.current?.loop.suspend();
        stopGPUProbe();
        if (defer) presentWebGLZoomSource(canvas);
        rendered.current = false;
        return;
      }
      const frame = canvas._fractalZoomCache?.frame;
      if (!isPan && !gpuPending.current && frame?.exact !== false &&
          rendered.current && !canvas._fractalZoomCache?.interactive &&
          !canvas._fractalZoomCache?.panPreview &&
          frame?.width === canvas.width && frame?.height === canvas.height &&
          frame?.paletteKey === JSON.stringify(paramsRef.current || {}) && sameRanges(frame, ranges)) return;
      drawGPU(canvas, ranges, isPan && interactive ? { cachePan: true, pan: true, anchor, presentationTime } :
        interactive ? { interactiveExact: true, anchor, presentationTime } :
          anchor || Number.isFinite(presentationTime) ? { anchor, presentationTime } : undefined);
      return;
    }

    if (cpuMotion.current && (!interactive ||
        cpuMotion.current.canvas !== canvas || cpuMotion.current.width !== canvas?.width ||
        cpuMotion.current.height !== canvas?.height)) {
      cpuMotion.current.loop.suspend();
      cpuMotion.current.loop.dispose();
      cpuMotion.current = null;
    }
    if (cpuMotion.current) {
      if (waitForVisibility) cpuMotion.current.loop.suspend();
      else {
        cpuMotion.current.ranges = copyRanges(ranges);
        cpuMotion.current.paletteKey = JSON.stringify(paramsRef.current || {});
        cpuMotion.current.loop.request(ranges, paramsRef.current,
          { ...cpuRequestOptions.current, presentationTime });
      }
      rendered.current = false;
      cpuPreview.current = true;
      return;
    }

    const image = completedImage.current;
    const sourceIsCurrent = canvas && image && image.width === canvas.width &&
      image.height === canvas.height && image.colorFunc === colorFuncRef.current;
    // Keep useful immutable CPU work alive while zoom input replaces only the
    // next request. Hard lifecycle changes and pan still cancel obsolete work.
    const compatibleJob = cpuJob.current?.canvas === canvas &&
      cpuJob.current.width === canvas.width && cpuJob.current.height === canvas.height &&
      cpuJob.current.colorFunc === colorFuncRef.current;
    if (isPan || pan.current || waitForVisibility || !sourceIsCurrent && !compatibleJob) {
      generation.current += 1;
      cancelRender();
    }
    if (!waitForVisibility && sourceIsCurrent) {
      if (sameRanges(completed.current, ranges)) {
        if (compatibleJob && !sameRanges(cpuJob.current.ranges, ranges)) {
          generation.current += 1;
          cancelRender();
        }
        canvas.style.transform = '';
        cpuPreview.current = false;
        rendered.current = true;
        return;
      }
      cpuPreview.current = scaleCanvasFromCamera(canvas, completed.current, ranges);
    } else if (waitForVisibility && canvas) {
      canvas.style.transform = '';
      cpuPreview.current = false;
    }
    pan.current = isPan && interactive;
    settledPan.current = isPan && !interactive;
    rendered.current = false;
    if (!waitForVisibility) scheduleCpuRender();
  }, [canvasRef, cancelRender, drawGPU, preferredRenderer, scheduleCpuRender, stopGPUProbe]);

  const reservePresentation = useCallback(() => {
    gpuMotion.current?.loop.reservePresentation?.();
    cpuMotion.current?.loop.reservePresentation?.();
  }, []);

  const suspend = useCallback(({ restoreSource = true } = {}) => {
    if (!enabled.current) return;
    gpuMotion.current?.loop.suspend(restoreSource ? undefined : { restoreSource: false });
    cpuMotion.current?.loop.suspend();
    stopGPUProbe();
    discardWebGLZoomTransition(canvasRef.current);
    if (canvasRef.current?._useWebGL && restoreSource) presentWebGLZoomSource(canvasRef.current);
    else if (canvasRef.current) {
      canvasRef.current.style.transform = '';
      cpuPreview.current = false;
    }
    enabled.current = false;
    generation.current += 1;
    cancelRender();
  }, [canvasRef, cancelRender, stopGPUProbe]);

  const restoreImage = useCallback((ranges, image) => {
    gpuMotion.current?.loop.suspend();
    cpuMotion.current?.loop.dispose();
    cpuMotion.current = null;
    stopGPUProbe();
    discardWebGLZoomTransition(canvasRef.current);
    enabled.current = true;
    requested.current = ranges;
    generation.current += 1;
    cancelRender();
    const context = canvasRef.current?.getContext('2d');
    clearCpuExactFrame(cpuExactOwner.current);
    if (image && context) {
      context.putImageData(image, 0, 0);
      completed.current = ranges;
      canvasRef.current.style.transform = '';
      cpuPreview.current = false;
      rendered.current = true;
      completedImage.current = { width: canvasRef.current.width, height: canvasRef.current.height, colorFunc: colorFuncRef.current };
      cpuSource.current = image.data instanceof Uint8ClampedArray ? {
        pixels: image.data, ranges, width: image.width, height: image.height,
        colorParams: { ...paramsRef.current }, cameraRequestedAt: 0, worker: false,
        cameraRequestedAtKnown: false, sourceTimeKnown: false,
      } : null;
      setRecovering(false);
      return true;
    }
    return false;
  }, [canvasRef, cancelRender, stopGPUProbe]);

  const canCaptureHistory = useCallback(ranges => {
    const canvas = canvasRef.current;
    const image = completedImage.current;
    const exactCpuMotion = cpuMotion.current?.loop.stats?.exactCurrent;
    return (cpuMotion.current ? exactCpuMotion : rendered.current && !cpuPreview.current) && image && canvas &&
      canvas._fractalZoomCache?.frame?.exact !== false && !canvas._fractalZoomCache?.interactive &&
      !canvas._fractalZoomCache?.panPreview &&
      !canvas._fractalZoomCache?.transition && image.width === canvas.width && image.height === canvas.height &&
      image.colorFunc === colorFuncRef.current && ['xRange', 'yRange'].every(axis =>
        completed.current?.[axis].every((value, index) => value === ranges[axis][index]));
  }, [canvasRef]);

  const getPresentedSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    const source = canvas?._fractalZoomCache?.frame;
    if (canvas?._useWebGL && source) {
      return source.exact !== false && source.paletteKey === JSON.stringify(paramsRef.current || {})
        ? cameraFromRanges(source.xRange, source.yRange).snapshot() : null;
    }
    return completedImage.current && completed.current
      ? cameraFromRanges(completed.current.xRange, completed.current.yRange).snapshot() : null;
  }, [canvasRef]);

  const projectOriginalSource = useCallback((camera, { presentationTime, forwardTransform, pendingCamera } = {}) => {
    const canvas = canvasRef.current;
    const frame = canvas?._fractalZoomCache?.frame;
    if (!canvas?._useWebGL || !frame || frame.exact === false || !camera && !forwardTransform) return false;
    const sourceCamera = getPresentedSnapshot();
    if (!sourceCamera) return false;
    try {
      const requestedCamera = camera?.snapshot() || null;
      const transform = forwardTransform || camera.viewTransform(sourceCamera);
      const target = requestedCamera || pendingCamera?.snapshot;
      if (!target) return false;
      const operations = pendingCamera?.operations || [];
      const zoomLog = operations.filter(operation => operation.type === 'zoom')
        .reduce((sum, operation) => sum + Math.log10(operation.args[0]), 0);
      const targetAspect = operations.filter(operation => operation.type === 'aspect').at(-1)?.args[0] || target.aspect;
      const spanLog = decimalLog10(sourceCamera.spanY) - decimalLog10(target.spanY) - zoomLog;
      const metadata = { sourceCamera, requestedCamera,
        ...(pendingCamera && { pendingCamera }),
        requestedCameraAt: Number.isFinite(presentationTime) ? presentationTime : performance.now(),
        presentedAt: Number.isFinite(presentationTime) ? presentationTime : performance.now(),
        paletteKey: JSON.stringify(paramsRef.current || {}),
        sampleFootprintLog10: [spanLog + Math.log10(sourceCamera.aspect / targetAspect) +
          Math.log10(canvas.width / frame.width), spanLog + Math.log10(canvas.height / frame.height)] };
      if (!presentWebGLOriginalSource(canvas, transform, metadata)) return false;
      canvas.style.transform = '';
      cpuPreview.current = false;
      rendered.current = false;
      canvas._fractalMotionStats = { ...canvas._fractalMotionStats,
        requestedRanges: null, requestedCamera, requestedCameraAt: metadata.requestedCameraAt,
        sourceProjection: canvas._fractalSourceProjection, exactCurrent: false };
      return true;
    } catch (error) { return false; }
  }, [canvasRef, getPresentedSnapshot]);

  useEffect(() => {
    mounted.current = true;
    const canvas = canvasRef.current;
    const exactOwner = cpuExactOwner.current;
    if (offloadedSnapshotRef.current) {
      const snap = offloadedSnapshotRef.current;
      offloadedSnapshotRef.current = null;
      if (!isCanvasVisible(canvas) && snap.width > 0 && snap.height > 0) {
        const context2d = canvas.getContext('2d');
        if (context2d?.drawImage) {
          context2d.drawImage(snap, 0, 0, canvas.width, canvas.height);
          canvas._webGLDecided = true;
          canvas._useWebGL = false;
          canvas._wasOffloadedWebGL = true;
        }
      }
      snap.width = 0;
      snap.height = 0;
    }
    const onContextLost = event => {
      if (canvas._webGLOffloaded) {
        event?.preventDefault?.();
        stopGPUProbe();
        gpuMotion.current?.loop.dispose();
        gpuMotion.current = null;
        offloadedSnapshotRef.current = canvas._offloadedSnapshot || null;
        delete canvas._offloadedSnapshot;
        rendered.current = false;
        setCanvasEpoch(value => value + 1);
        return;
      }
      // A WebGL canvas cannot switch to 2D after losing its context. Replace
      // it so the existing CPU renderer can redraw the current view.
      if (canvas._useWebGL) fallback(canvas, requested.current);
    };
    canvas.addEventListener('webglcontextlost', onContextLost);
    return () => {
      mounted.current = false;
      gpuMotion.current?.loop.dispose();
      gpuMotion.current = null;
      cpuMotion.current?.loop.dispose();
      cpuMotion.current = null;
      stopGPUProbe();
      if (cpuRenderFrame.current !== null) cancelAnimationFrame(cpuRenderFrame.current);
      cpuRenderFrame.current = null;
      generation.current += 1;
      cancelRender();
      clearCpuExactFrame(exactOwner);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      disposeWebGLPanCache(canvas);
      disposeWebGLZoomCache(canvas);
      disposeWebGLResources?.(canvas);
    };
  }, [canvasRef, canvasEpoch, cancelRender, fallback, stopGPUProbe]);

  useEffect(() => {
    gpuMotion.current?.loop.dispose();
    gpuMotion.current = null;
    cpuMotion.current?.loop.dispose();
    cpuMotion.current = null;
    stopGPUProbe();
    discardWebGLZoomTransition(canvasRef.current);
    rendered.current = false;
    generation.current += 1;
    cancelRender();
    clearCpuExactFrame(cpuExactOwner.current);
    cpuSource.current = null;
  }, [colorFunc, colorParams, paletteParams, cpuReusePaletteArray, cancelRender, stopGPUProbe, canvasRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    let disposed = false;
    let running = false;
    const render = async event => {
      if (!enabled.current || rendered.current || running || disposed) return;
      if (canvas._wasOffloadedWebGL && preferredRenderer === 'webgl' && !forceCanvas.current) {
        if (!isCanvasVisible(canvas)) return;
        canvas._wasOffloadedWebGL = false;
        rendered.current = false;
        setCanvasEpoch(value => value + 1);
        return;
      }
      running = true;
      const job = generation.current;
      const jobWidth = canvas.width, jobHeight = canvas.height;
      const stale = () => disposed || !mounted.current || !enabled.current || job !== generation.current ||
        canvas !== canvasRef.current || canvas.width !== jobWidth || canvas.height !== jobHeight;
      const ranges = requested.current;
      if (!ranges) { running = false; return; }
      const workerColorParams = colorParams ? { ...colorParams } : colorParams;
      // Match the key to the frozen values sent to every worker tile. A
      // supplied palette object may be mutated in place during an async job.
      const paletteKeyAtStart = JSON.stringify(workerColorParams);
      const paletteCertifiable = cpuExactPanReuse && paletteParams &&
        canCertifyCpuPalette(workerColorParams);
      const paletteChanged = () => paletteCertifiable &&
        (!canCertifyCpuPalette(colorParams) ||
          paletteKeyAtStart !== JSON.stringify(colorParams));
      if (canvas._webGLDecided === undefined) {
        canvas._webGLDecided = true;
        canvas._fractalContext = preferredRenderer === 'webgl' && !forceCanvas.current
          ? getWebGLContext(canvas) : null;
        canvas._useWebGL = !!canvas._fractalContext;
      }
      if (canvas._useWebGL && !forceCanvas.current) {
        if (needsBoundedGpuMotion(canvas, ranges, gpuRequestOptions.current, colorParams) &&
            sameRanges(gpuMotion.current?.ranges, ranges) &&
            gpuMotion.current.paletteKey === JSON.stringify(colorParams || {}))
          gpuMotion.current.loop.resume?.();
        else drawGPU(canvas, ranges, gpuRequestOptions.current);
        running = false;
        return;
      }
      const cached = getCpuExactFrame(cpuExactOwner.current);
      const seed = cpuSource.current || (cached && sameRanges(cached.ranges, completed.current)
        ? { ...cached, colorParams, worker: true } : null);
      const hasSeed = seed?.width === canvas.width && seed?.height === canvas.height &&
        sameRanges(seed.ranges, completed.current) && JSON.stringify(seed.colorParams) === JSON.stringify(colorParams);
      if (cpuRequestOptions.current.interactive && (!pan.current || hasSeed) &&
          !(settledPan.current && cpuExactPanReuse) &&
          !canvas._fractalCpuMotionFailed) {
        if (!cpuMotion.current) {
          const loop = createCpuMotionLoop(canvas, { renderFull, renderTile, cancelRender,
            shouldUseWorker, colorFunc, paletteParams, cpuReusePaletteArray, cpuInteriorDispatch,
            isVisible: isCanvasVisible,
            initialSource: hasSeed ? { pixels: seed.pixels, width: seed.width, height: seed.height,
                ...seed.ranges, colorParams, worker: seed.worker,
                cameraRequestedAt: seed.cameraRequestedAt ?? 0,
                cameraRequestedAtKnown: seed.cameraRequestedAtKnown !== false &&
                  seed.sourceTimeKnown !== false && Number.isFinite(seed.cameraRequestedAt),
                sourceTimeKnown: seed.cameraRequestedAtKnown !== false &&
                  seed.sourceTimeKnown !== false && Number.isFinite(seed.cameraRequestedAt) } : null,
            onFrame: frame => {
              if (cpuMotion.current?.loop !== loop || disposed || !enabled.current || canvas !== canvasRef.current ||
                  frame.width !== canvas.width || frame.height !== canvas.height) return;
              completed.current = frame.ranges;
              cpuSource.current = frame;
              completedImage.current = { width: frame.width, height: frame.height, colorFunc };
              cpuPreview.current = !frame.current;
              rendered.current = !!frame.current && sameRanges(frame.ranges, requested.current);
              if (cpuExactPanReuse && frame.worker && canCertifyCpuPalette(frame.colorParams))
                setCpuExactFrame(cpuExactOwner.current, { pixels: frame.pixels,
                  ranges: frame.ranges, width: frame.width, height: frame.height,
                  paletteKey: JSON.stringify(frame.colorParams), paletteStyle: frame.colorParams.style,
                  sourceKind: 'complete-worker-grid-rgba8', iterationBudget: MAX_ITERATIONS,
                  smoothingSteps: SMOOTHING_STEPS,
                  cameraRequestedAt: frame.cameraRequestedAt,
                  workerRoute: workerRenderRoute(frame.ranges.xRange, frame.ranges.yRange, frame.colorParams) });
              else clearCpuExactFrame(cpuExactOwner.current);
              pan.current = settledPan.current = false;
              setRecovering(false);
              onCpuFrameReadyRef.current?.({ width: frame.width, height: frame.height });
            },
            onError: error => {
              if (cpuMotion.current?.loop !== loop) return;
              console.warn('Progressive CPU render failed:', error);
              cpuMotion.current = null;
              canvas._fractalCpuMotionFailed = true;
              rendered.current = false;
              if (!disposed) scheduleCpuRender();
            },
          });
          if (loop) cpuMotion.current = { canvas, width: canvas.width, height: canvas.height, loop };
        }
        if (cpuMotion.current) {
          if (sameRanges(cpuMotion.current.ranges, ranges) &&
              cpuMotion.current.paletteKey === JSON.stringify(colorParams || {}))
            cpuMotion.current.loop.resume?.();
          else {
            cpuMotion.current.ranges = copyRanges(ranges);
            cpuMotion.current.paletteKey = JSON.stringify(colorParams || {});
            cpuMotion.current.loop.request(ranges, colorParams,
              { ...cpuRequestOptions.current, presentationTime: event?.detail?.presentationTime });
          }
          running = false;
          return;
        }
      }
      // NumberCamera supplies fresh arrays, but direct hook callers may edit
      // their requested arrays while worker tiles are in flight. Give every
      // tile one immutable numerical grid and decline a stale live request.
      const workerRanges = Object.freeze({
        xRange: Object.freeze([ranges.xRange[0], ranges.xRange[1]]),
        yRange: Object.freeze([ranges.yRange[0], ranges.yRange[1]])
      });
      const { xRange, yRange } = workerRanges;
      const cameraRequestedAt = performance.now();
      const rangesChanged = () => workerRanges !== ranges &&
        (!Object.is(ranges.xRange?.[0], xRange[0]) ||
          !Object.is(ranges.xRange?.[1], xRange[1]) ||
          !Object.is(ranges.yRange?.[0], yRange[0]) ||
          !Object.is(ranges.yRange?.[1], yRange[1]));
      const drawCanvas = () => {
        if (cpuReusePaletteArray && paletteParams) {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc,
            colorParams?.useMu !== false, paletteParams);
        } else if (colorParams?.useMu === false) {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc, false);
        } else {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc);
        }
      };
      const context = canvas.getContext('2d');
      if (!context) { running = false; return; }
      const activeJob = { canvas, width: jobWidth, height: jobHeight, colorFunc, ranges: workerRanges };
      cpuJob.current = activeJob;
      const previous = completed.current;
      const panCanvas = () => colorParams?.useMu === false
        ? applyPanAndRenderEdges(canvas, previous.xRange, previous.yRange, xRange, yRange, colorFunc, false)
        : applyPanAndRenderEdges(canvas, previous.xRange, previous.yRange, xRange, yRange, colorFunc);
      const useWorker = shouldUseWorker(canvas.width, canvas.height);
      const settledRequest = settledPan.current;
      const exactSource = cpuExactPanReuse ? getCpuExactFrame(cpuExactOwner.current) : null;
      const exactPlan = paletteCertifiable && !paletteChanged() && !rangesChanged() &&
        settledRequest && useWorker &&
        exactSource?.sourceKind === 'complete-worker-grid-rgba8' &&
        exactSource.iterationBudget === MAX_ITERATIONS &&
        exactSource.smoothingSteps === SMOOTHING_STEPS &&
        exactSource.paletteStyle === workerColorParams.style &&
        exactSource.width === canvas.width && exactSource.height === canvas.height &&
        exactSource.pixels instanceof Uint8ClampedArray &&
        exactSource.pixels.length === canvas.width * canvas.height * 4 &&
        exactSource.paletteKey === paletteKeyAtStart &&
        exactSource.workerRoute === workerRenderRoute(xRange, yRange, workerColorParams)
        ? planExactCpuPan(exactSource.ranges, workerRanges, canvas.width, canvas.height) : null;
      const reusablePlan = exactPlan && exactPlan.reusablePixels >= exactPlan.totalPixels * 0.5
        ? exactPlan : null;
      let exactPixels = null;
      let exactWork = null;
      try {
        if (reusablePlan) {
          const result = await renderExactPan({ canvasWidth: canvas.width, canvasHeight: canvas.height,
            sourcePixels: exactSource.pixels, dx: reusablePlan.dx, dy: reusablePlan.dy,
            cleanX: reusablePlan.cleanX, cleanY: reusablePlan.cleanY,
            newXRange: xRange, newYRange: yRange, colorParams: workerColorParams });
          if (stale() || rangesChanged()) return;
          if (paletteChanged()) throw new Error('Palette changed during exact CPU pan');
          const pixels = result.pixels;
          if (pixels.length !== canvas.width * canvas.height * 4 ||
              result.computedPixels !== reusablePlan.totalPixels - reusablePlan.reusablePixels) {
            throw new Error('Exact-pan assembled frame count mismatch');
          }
          context.putImageData(new ImageData(pixels, canvas.width, canvas.height), 0, 0);
          exactPixels = pixels;
          exactWork = { workerTiles: result.workerTiles, computedPixels: result.computedPixels };
        } else if (pan.current && previous) {
          if (useWorker) {
            const result = await renderPan({ canvasWidth: canvas.width, canvasHeight: canvas.height,
              oldXRange: previous.xRange, oldYRange: previous.yRange,
              newXRange: xRange, newYRange: yRange, colorParams: workerColorParams });
            if (stale() || rangesChanged()) return;
            if (paletteChanged()) throw new Error('Palette changed during CPU pan preview');
            presentPanTiles(canvas, context, result);
          } else if (!panCanvas()) {
            drawCanvas();
          }
        } else if (useWorker) {
          const pixels = await renderFull({ canvasWidth: canvas.width, canvasHeight: canvas.height,
            xRange, yRange, colorParams: workerColorParams, cpuInteriorDispatch });
          if (stale() || rangesChanged()) return;
          if (paletteChanged()) throw new Error('Palette changed during full CPU render');
          context.putImageData(new ImageData(pixels, canvas.width, canvas.height), 0, 0);
          exactPixels = pixels;
        } else {
          drawCanvas();
          clearCpuExactFrame(cpuExactOwner.current);
        }
      } catch (error) {
        if (stale() || rangesChanged() || error.name === 'AbortError') return;
        console.warn('Worker render failed, falling back to main thread:', error);
        drawCanvas();
        clearCpuExactFrame(cpuExactOwner.current);
      } finally {
        running = false;
        if (cpuJob.current === activeJob) cpuJob.current = null;
        if (!disposed && enabled.current && !sameRanges(requested.current, workerRanges)) scheduleCpuRender();
      }
      if (!stale() && !rangesChanged()) {
        if (paletteCertifiable && exactPixels && !paletteChanged())
          setCpuExactFrame(cpuExactOwner.current,
          { pixels: exactPixels, ranges: workerRanges, width: canvas.width, height: canvas.height,
            paletteKey: paletteKeyAtStart,
            paletteStyle: workerColorParams.style,
            sourceKind: 'complete-worker-grid-rgba8',
            iterationBudget: MAX_ITERATIONS, smoothingSteps: SMOOTHING_STEPS, cameraRequestedAt,
            workerRoute: workerRenderRoute(xRange, yRange, workerColorParams) });
        cpuSource.current = exactPixels ? { pixels: exactPixels, ranges: workerRanges,
          width: canvas.width, height: canvas.height, colorParams: workerColorParams,
          cameraRequestedAt, worker: true } : null;
        completed.current = workerRanges;
        const currentCamera = sameRanges(requested.current, workerRanges);
        canvas.style.transform = '';
        cpuPreview.current = !currentCamera && scaleCanvasFromCamera(canvas, workerRanges, requested.current);
        rendered.current = currentCamera;
        completedImage.current = { width: canvas.width, height: canvas.height, colorFunc };
        pan.current = false;
        settledPan.current = false;
        setRecovering(false);
        onCpuFrameReadyRef.current?.(cpuExactPanReuse
          ? { width: canvas.width, height: canvas.height, settledPan: settledRequest,
            exactPan: exactWork ? { reusablePixels: reusablePlan.reusablePixels,
              totalPixels: reusablePlan.totalPixels, dx: reusablePlan.dx, dy: reusablePlan.dy,
              dirtyTransferRGBABytes: exactWork.computedPixels * 4,
              ...exactWork } : null }
          : { width: canvas.width, height: canvas.height });
      }
    };
    canvas.addEventListener('render', render);
    if (canvasEpoch > 0) {
      const rect = canvas.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) render();
    }
    return () => {
      disposed = true;
      if (cpuJob.current?.canvas === canvas) cpuJob.current = null;
      canvas.removeEventListener('render', render);
      cancelRender();
    };
  }, [canvasRef, canvasEpoch, colorFunc, colorParams, paletteParams,
    cpuReusePaletteArray, cpuExactPanReuse, cpuInteriorDispatch, preferredRenderer,
    shouldUseWorker, renderFull, renderTile, renderPan, renderExactPan, cancelRender, drawGPU, scheduleCpuRender]);

  return { canvasEpoch, recovering, present, reservePresentation, suspend, restoreImage,
    canCaptureHistory, getPresentedSnapshot, projectOriginalSource };
}
